import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, BellRing, Loader2, Mail, Save } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const usd = (v, dp = 4) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;

const FIELDS = [
  { key: "per_video_usd", label: "Per-video cost", hint: "alert when one video costs more" },
  { key: "daily_usd", label: "Daily AI spend", hint: "alert when today crosses this" },
  { key: "monthly_usd", label: "Monthly AI spend", hint: "alert when this month crosses this" },
];

export default function SpendAlerts() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ per_video_usd: "", daily_usd: "", monthly_usd: "", enabled: true });
  const [busy, setBusy] = useState(false);

  const hydrate = useCallback((data) => {
    setState(data);
    setForm({
      per_video_usd: data.config.per_video_usd ?? "",
      daily_usd: data.config.daily_usd ?? "",
      monthly_usd: data.config.monthly_usd ?? "",
      enabled: data.config.enabled !== false,
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/spend/alerts");
      hydrate(data);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error("Could not load cost alerts");
    }
  }, [hydrate]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        enabled: form.enabled,
        per_video_usd: form.per_video_usd === "" ? null : Number(form.per_video_usd),
        daily_usd: form.daily_usd === "" ? null : Number(form.daily_usd),
        monthly_usd: form.monthly_usd === "" ? null : Number(form.monthly_usd),
      };
      const { data } = await api.post("/admin/spend/alerts", body);
      hydrate(data);
      toast.success("Cost alert thresholds saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save thresholds");
    } finally {
      setBusy(false);
    }
  };

  const checkNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/spend/alerts/check");
      hydrate(data);
      const n = (data.notified || []).length;
      toast.success(n ? `${n} new breach(es) notified by email` : "No new breaches to notify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Alert check failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const breaches = state.breaches || [];

  return (
    <section data-testid="spend-alerts-panel">
      <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Cost alerts</h2>
      <div className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-5">
        <div className="flex flex-wrap items-end gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
                {f.label} (USD)
              </div>
              <input
                value={form[f.key]}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder="off"
                inputMode="decimal"
                data-testid={`alert-input-${f.key}`}
                className="h-9 px-3 w-28 bg-zinc-950 border border-zinc-800 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="text-[10px] font-mono text-zinc-600 mt-1">{f.hint}</div>
            </div>
          ))}

          <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((s) => ({ ...s, enabled: e.target.checked }))}
              data-testid="alert-enabled-toggle"
              className="accent-emerald-500"
            />
            Enabled
          </label>

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="alert-save-btn"
              className="h-9 px-3 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-300 inline-flex items-center gap-1.5 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
            <button
              type="button"
              onClick={checkNow}
              disabled={busy}
              data-testid="alert-check-btn"
              className="h-9 px-3 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 inline-flex items-center gap-1.5 disabled:opacity-40"
            >
              <BellRing className="w-3 h-3" /> Check &amp; notify
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-6 text-[11px] font-mono text-zinc-500">
          <span data-testid="alert-day-spend">
            Today: <span className="text-white">{usd(state.day_usd)}</span>
            {state.config.daily_usd ? ` / ${usd(state.config.daily_usd, 2)}` : " · no limit"}
          </span>
          <span data-testid="alert-month-spend">
            {state.window?.month}: <span className="text-white">{usd(state.month_usd)}</span>
            {state.config.monthly_usd ? ` / ${usd(state.config.monthly_usd, 2)}` : " · no limit"}
          </span>
          <span className="inline-flex items-center gap-1.5" data-testid="alert-email-status">
            <Mail className="w-3 h-3" />
            {state.email_configured
              ? `email to ${(state.recipients || []).join(", ") || "no admin address"}`
              : "email not configured (Resend key missing)"}
          </span>
        </div>

        {breaches.length > 0 ? (
          <div className="border border-red-500/30 bg-red-500/[0.04]" data-testid="alert-breach-list">
            <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-red-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {breaches.length} threshold breach(es) currently detected
            </div>
            <table className="w-full text-xs">
              <tbody>
                {breaches.map((b) => (
                  <tr key={b.alert_key} className="border-t border-red-500/15">
                    <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-red-300">
                      {b.kind.replace("_", " ")}
                    </td>
                    <td className="px-3 py-2 text-zinc-300">{b.detail}</td>
                    <td className="px-3 py-2 text-right font-mono text-white">{usd(b.value_usd)}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-500">
                      limit {usd(b.threshold_usd, 2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-zinc-500" data-testid="alert-no-breaches">
            No threshold is currently breached.
          </p>
        )}

        {(state.history || []).length > 0 && (
          <div className="border border-zinc-800" data-testid="alert-history">
            <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              Alert history (deduplicated — each breach notifies once)
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                {state.history.map((h) => (
                  <tr key={h.alert_key} className="border-t border-zinc-800/60">
                    <td className="px-3 py-1.5 text-zinc-500 font-mono">
                      {h.first_seen_at ? new Date(h.first_seen_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                      {h.kind}
                    </td>
                    <td className="px-3 py-1.5 text-zinc-400">{h.detail}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-500">
                      {h.notified ? "emailed" : "recorded"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[10px] font-mono text-zinc-600">{state.note}</p>
      </div>
    </section>
  );
}
